import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useEffect, useRef, useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';
import type { DealTag } from '@platform/shared-types';
import { newTagId, tagTextColor } from './dealsListModel';

/**
 * THE TAG RAIL — filter by tag, and author the tags themselves, in one place.
 *
 * Tags exist because an operator needed one while looking at a deal. Sending
 * them to a settings screen to make one guarantees they will instead reuse a
 * tag that nearly fits, and the grouping stops meaning anything. So the rail
 * that filters is also the rail that creates, renames, recolours and deletes.
 *
 * Nothing here is destructive to a deal: deleting a tag removes the label from
 * the registry, and deals carrying its id simply stop rendering it.
 */

const SWATCHES = [
  '#fde68a', '#bbf7d0', '#bfdbfe', '#e9d5ff',
  '#fecdd3', '#cffafe', '#fed7aa', '#e7e5e4',
];

/** The sticky-note chip. One definition, used by the rail and by every deal row. */
export const TagChip: React.FC<{
  tag: DealTag;
  size?: 'sm' | 'md';
  onClick?: () => void;
  dimmed?: boolean;
  title?: string;
}> = ({ tag, size = 'sm', onClick, dimmed, title }) => (
  <span
    onClick={onClick}
    title={title || tag.label}
    role={onClick ? 'button' : undefined}
    className={`relative inline-flex items-center gap-1 rounded font-bold leading-[1.45] tracking-[.01em]
      shadow-[0_1px_2px_rgba(28,25,23,.13)] transition-transform duration-100
      ${size === 'sm' ? 'px-2 py-[2.5px] text-[10.5px]' : 'px-3 py-[5px] text-[11.5px]'}
      ${onClick ? 'cursor-pointer hover:-translate-y-px hover:-rotate-1' : ''}
      ${dimmed ? 'opacity-55 saturate-[.25]' : ''}`}
    style={{ background: tag.color, color: tagTextColor(tag.color) }}
  >
    {tag.label}
    {/* the folded corner that makes it read as paper rather than as a pill */}
    <span
      aria-hidden
      className="pointer-events-none absolute bottom-0 right-0 h-[7px] w-[7px]"
      style={{ background: 'linear-gradient(135deg,transparent 50%,rgba(0,0,0,.14) 50%)' }}
    />
  </span>
);

interface EditorState { id: string | null; label: string; color: string }

const DealTagRail: React.FC<{
  tags: DealTag[];
  selectedIds: string[];
  onToggle: (tagId: string) => void;
  onClear: () => void;
  /** Replaces the whole registry — see `saveTags`. Rejects to leave state untouched. */
  onSave: (next: DealTag[]) => Promise<void>;
}> = ({ tags, selectedIds, onToggle, onClear, onSave }) => {
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // A click outside closes the editor. Nothing is saved on close: a
  // half-finished tag is discarded rather than written.
  useEffect(() => {
    if (!editor) return undefined;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setEditor(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [editor]);

  const commit = async (next: DealTag[]) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditor(null);
    } catch (e: any) {
      setError(e?.message || 'Could not save tags');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const label = editor?.label.trim();
    if (!editor || !label) return;
    const next = editor.id
      ? tags.map((t) => (t.id === editor.id ? { ...t, label, color: editor.color } : t))
      : [...tags, { id: newTagId(), label, color: editor.color }];
    await commit(next);
  };

  /**
   * Dropping the tag from the registry IS the delete. The ids stay on whatever
   * deals carried them and stop resolving, so no deal is written and none can
   * be damaged by a half-finished sweep.
   */
  const remove = async () => {
    if (!editor?.id) return;
    await commit(tags.filter((t) => t.id !== editor.id));
    if (selectedIds.includes(editor.id)) onToggle(editor.id);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-3 md:px-4">
      <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-stone-400">Tags</span>

      {tags.map((tag) => {
        const on = selectedIds.includes(tag.id);
        return (
          <span key={tag.id} className="group relative inline-flex items-center">
            <TagChip
              tag={tag}
              size="md"
              dimmed={selectedIds.length > 0 && !on}
              onClick={() => onToggle(tag.id)}
              title={on ? `Stop filtering by ${tag.label}` : `Filter by ${tag.label}`}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setError(null);
                setEditor({ id: tag.id, label: tag.label, color: tag.color });
              }}
              title={`Edit ${tag.label}`}
              className="ml-0.5 rounded p-0.5 text-stone-300 opacity-0 transition group-hover:opacity-100 hover:bg-stone-100 hover:text-stone-600"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
            </button>
          </span>
        );
      })}

      <div className="relative">
        <button
          onClick={() => { setError(null); setEditor({ id: null, label: '', color: SWATCHES[0] }); }}
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-stone-300 px-2.5 py-1.5
                     text-[11.5px] font-semibold text-stone-500 transition hover:border-rose-400 hover:text-rose-600"
        >
          <Plus className="h-3 w-3" /> New tag
        </button>

        {editor && (
          <div
            ref={popoverRef}
            className="absolute left-0 z-30 mt-2 w-64 rounded-xl border border-stone-200 bg-white p-3 shadow-xl"
          >
            <input
              autoFocus
              value={editor.label}
              maxLength={40}
              placeholder="Tag name"
              onChange={(e) => setEditor({ ...editor, label: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditor(null); }}
              className="w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm outline-none focus:border-rose-400"
            />

            <div className="mt-2.5 grid grid-cols-8 gap-1.5">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  onClick={() => setEditor({ ...editor, color: c })}
                  title={c}
                  style={{ background: c }}
                  className={`h-6 rounded transition ${
                    editor.color === c ? 'ring-2 ring-stone-800 ring-offset-1' : 'hover:scale-105'
                  }`}
                />
              ))}
            </div>

            {editor.label.trim() && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Preview</span>
                <TagChip tag={{ id: 'preview', label: editor.label.trim(), color: editor.color }} size="md" />
              </div>
            )}

            {error && <p className="mt-2 text-[10.5px] font-medium text-red-600">{error}</p>}

            <div className="mt-3 flex gap-2">
              <button
                onClick={save}
                disabled={saving || !editor.label.trim()}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-rose-600 py-2 text-[12.5px]
                           font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
              >
                {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Check className="h-3.5 w-3.5" />}
                {editor.id ? 'Save' : 'Create tag'}
              </button>
              {editor.id && (
                <button
                  onClick={remove}
                  disabled={saving}
                  title="Delete this tag"
                  className="rounded-lg border border-stone-200 px-2.5 text-stone-400 transition
                             hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <p className="mt-2 text-[10.5px] leading-snug text-stone-400">
              Deleting a tag only removes the label — no deal is changed or deleted.
            </p>
          </div>
        )}
      </div>

      {selectedIds.length > 0 && (
        <button
          onClick={onClear}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-stone-500 hover:bg-stone-100"
        >
          <X className="h-3 w-3" /> Clear {selectedIds.length}
        </button>
      )}

      <span className="ml-auto hidden text-[11px] text-stone-400 lg:inline">
        Click a tag to filter · hover to rename, recolour or delete
      </span>
    </div>
  );
};

export default DealTagRail;
